/**
 * DerivedSectionCard — modern always-inline SectionCard
 * (SPEC.md Addendum 6 §6.4 D-INLINE-EDIT, Bill 2026-06-03/-04).
 *
 * SUPERSEDES the Slot-4 / BACKLOG-0574 Edit/Done accordion. Bill: "read-only
 * with expand/collapse edit sections is not modern, not elegant, junky." The
 * card no longer hides fields behind an Edit toggle. In the normal (editable)
 * modal EVERY section renders its fields inline, always editable — space comes
 * from DENSITY, not from collapsing. The inline sections ARE the review
 * surface (§6.1 — there is no separate read-only "Review" view).
 *
 * `readOnly` mode (a genuinely view-only modal) still renders the derived
 * definition-list `rows`, since there are no inputs to show there.
 *
 * The `editing` / `onToggleEdit` / `defaultEditing` props are now VESTIGIAL in
 * editable mode (accepted for call-site compatibility, ignored). They get
 * removed when IndividualReviewBody is restructured for jump-scroll (§6.6 step
 * 3). `rows` is still consumed in `readOnly` mode.
 *
 * Review-add ceremony (D2 / D-MODE-ORDER): the per-section
 * "Mark Reviewed Correct" footer button (amber unmarked → green marked) is
 * retained, shown only in the existing-CIF review-add flow.
 *
 * This card WRAPS the existing section components (Identity / Address / … —
 * owned by other slots) rather than rewriting them.
 */
import type { ReactNode } from 'react';

export interface DerivedRow {
  label: string;
  value: ReactNode;
  /** Visually flag a changed/overridden field (blue rail). */
  changed?: boolean;
}

export interface DerivedSectionCardProps {
  /** Section title (rendered UPPERCASE per DESIGN-BIBLE). */
  title: string;
  /** Derived field rows — rendered ONLY in `readOnly` (view-only) mode. */
  rows: ReadonlyArray<DerivedRow>;
  /** The inline-editable section body. Always rendered when not `readOnly`. */
  children?: ReactNode;
  /** Optional banner (e.g., ID-expired alert) rendered above the body. */
  banner?: ReactNode;
  /** Fully view-only modal — render the derived `rows` instead of inputs. */
  readOnly?: boolean;
  /** D2 review-add ceremony — show the per-section Mark Reviewed Correct btn. */
  showMarkReviewed?: boolean;
  reviewed?: boolean;
  onToggleReviewed?: () => void;
  /** @deprecated vestigial — ignored in editable mode (D-INLINE-EDIT). */
  defaultEditing?: boolean;
  /** @deprecated vestigial — ignored in editable mode (D-INLINE-EDIT). */
  editing?: boolean;
  /** @deprecated vestigial — ignored in editable mode (D-INLINE-EDIT). */
  onToggleEdit?: () => void;
}

const HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderBottom: '1px solid var(--ink-100,#f1f3f5)',
};

export function DerivedSectionCard({
  title,
  rows,
  children,
  banner,
  readOnly = false,
  showMarkReviewed = false,
  reviewed = false,
  onToggleReviewed,
}: DerivedSectionCardProps): JSX.Element {
  // D-INLINE-EDIT: editable modal → always show the inline fields (no Edit/Done,
  // no collapse). View-only modal → show the read-only derived rows.
  const showInline = !readOnly && !!children;

  return (
    <section
      style={{
        border: '1px solid var(--ink-200,#e9ecef)',
        borderRadius: 8,
        background: 'var(--surface-card,#fff)',
        overflow: 'hidden',
      }}
    >
      <div style={HEADER}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--pcb-blue-dark)',
          }}
        >
          {title}
        </span>
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {banner}

        {showInline ? (
          children
        ) : rows.length > 0 ? (
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(140px,auto) 1fr', rowGap: 6, columnGap: 16 }}>
            {rows.map((r, i) => (
              <div key={`${r.label}-${i}`} style={{ display: 'contents' }}>
                <dt style={{ fontSize: 12, color: 'var(--ink-600,#6c757d)' }}>{r.label}</dt>
                <dd
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: 'var(--ink-900,#212529)',
                    ...(r.changed ? { borderLeft: '3px solid var(--pcb-blue,#2968ad)', paddingLeft: 8 } : {}),
                  }}
                >
                  {r.value === '' || r.value == null ? (
                    <span style={{ color: 'var(--ink-400,#adb5bd)' }}>— not set —</span>
                  ) : (
                    r.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--ink-500,#868e96)', fontStyle: 'italic' }}>
            Nothing captured yet.
          </div>
        )}

        {showMarkReviewed && onToggleReviewed && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onToggleReviewed}
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: '5px 12px',
                borderRadius: 6,
                cursor: 'pointer',
                border: reviewed ? '1px solid var(--ok-fill)' : '1px solid var(--warn-text)',
                background: reviewed ? 'var(--ok-fill)' : 'var(--warn-bg)',
                color: reviewed ? 'var(--on-status-fill)' : 'var(--warn-text)',
              }}
            >
              {reviewed ? '✓ Reviewed correct' : 'Mark reviewed correct'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
